import React, { useState, useEffect } from 'react';
import api from './api';

const RoughSale = () => {
    // Lists
    const [buyers, setBuyers] = useState([]);
    const [brokers, setBrokers] = useState([]); // New List
    const [stock, setStock] = useState([]);
    
    // Form Data
    const [form, setForm] = useState({
        saleDate: new Date().toISOString().split('T')[0],
        buyerId: '',
        brokerId: '', // New Field
        lotId: '',
        
        // Auto-filled from Lot (For Preview)
        weight: '',
        costRate: '', 
        costTotal: '',
        origin: '',      // New
        sieveSize: '',   // New
        assortment: '',  // New

        // Sale Entry
        saleRate: '',
        currency: 'INR',
        exchangeRate: 1,
        
        billType: 'Bill',
        paymentTermsDays: '',
        taxPercent: 0,
        extraCharges: 0
    });

    const [calc, setCalc] = useState({ finalAmount: 0, profit: 0, profitPercent: 0 });

    // 1. Load Data
    useEffect(() => {
        const loadData = async () => {
            try {
                const b = await api.get('/buyers');
                setBuyers(b.data);

                const br = await api.get('/brokers'); // Fetch Brokers
                setBrokers(br.data);

                const s = await api.get('/rough');
                // Only show 'Rough' status lots
                setStock(s.data.data.filter(l => l.status === 'Rough'));
            } catch (err) { console.error("Error loading data"); }
        };
        loadData();
    }, []);

    // 2. Handle Defaults
    useEffect(() => {
        if(form.buyerId) {
            const b = buyers.find(x => x._id === form.buyerId);
            if(b && form.paymentTermsDays === '') {
                setForm(prev => ({ 
                    ...prev, 
                    paymentTermsDays: b.defaultTerms,
                    currency: b.defaultCurrency || 'INR'
                }));
            }
        }
        
        if(form.billType === 'Cash') {
            setForm(prev => ({ ...prev, taxPercent: 0 }));
        } else if (form.billType === 'Bill' && form.taxPercent === 0) {
            setForm(prev => ({ ...prev, taxPercent: 0.25 }));
        }
    }, [form.buyerId, form.billType, buyers]);


    // 3. Math Engine
    useEffect(() => {
        const w = parseFloat(form.weight) || 0;
        const rate = parseFloat(form.saleRate) || 0;
        const ex = form.currency === 'USD' ? (parseFloat(form.exchangeRate) || 1) : 1;
        
        const base = w * rate * ex;
        const tax = base * (parseFloat(form.taxPercent) / 100);
        const extra = parseFloat(form.extraCharges) || 0;
        const final = base + tax + extra;

        const cost = parseFloat(form.costTotal) || 0;
        const profitVal = base - cost;
        const profitPct = cost > 0 ? (profitVal / cost) * 100 : 0;

        setCalc({ finalAmount: final, profit: profitVal, profitPercent: profitPct });

    }, [form]);

    const handleLotSelect = (e) => {
        const lotId = e.target.value;
        const lot = stock.find(l => l._id === lotId);
        
        if (lot) {
            setForm(prev => ({
                ...prev,
                lotId: lotId,
                weight: lot.weight,
                costRate: lot.costPerCarat || lot.rate,
                costTotal: lot.landedCost,
                // Fill Details
                origin: lot.origin || '-',
                sieveSize: lot.sieveSize || '-',
                assortment: lot.assortment || '-'
            }));
        } else {
            setForm(prev => ({ ...prev, lotId: '', weight: '', costRate: '', costTotal: '', origin:'', sieveSize:'', assortment:'' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post('/sales', form);
            alert(`✅ Sold! Invoice: ${res.data.data.saleId}`);
            window.location.reload(); 
        } catch (err) {
            alert("Error: " + err.response?.data?.error);
        }
    };

    return (
        <div className="container mt-4 mb-5">
            <h4 className="fw-bold mb-3">New Rough Sale</h4>
            
            <div className="card shadow-sm border-0">
                <div className="card-header bg-dark text-white">Invoice Details</div>
                <div className="card-body bg-light">
                    <form onSubmit={handleSubmit}>
                        
                        {/* ROW 1: HEADER */}
                        <div className="row mb-3">
                            <div className="col-6 col-md-3">
                                <label className="small fw-bold">Date</label>
                                <input type="date" className="form-control" value={form.saleDate} onChange={e => setForm({...form, saleDate: e.target.value})} required />
                            </div>
                            <div className="col-6 col-md-2">
                                <label className="small fw-bold">Bill Type</label>
                                <select className="form-select" value={form.billType} onChange={e => setForm({...form, billType: e.target.value})}>
                                    <option>Bill</option><option>Cash</option>
                                </select>
                            </div>
                            <div className="col-6 col-md-3">
                                <label className="small fw-bold">Buyer</label>
                                <select className="form-select" value={form.buyerId} onChange={e => setForm({...form, buyerId: e.target.value})} required>
                                    <option value="">Select Customer...</option>
                                    {buyers.map(b => <option key={b._id} value={b._id}>{b.companyName}</option>)}
                                </select>
                            </div>
                            <div className="col-6 col-md-2">
                                <label className="small fw-bold">Broker (Ref)</label>
                                <select className="form-select" value={form.brokerId} onChange={e => setForm({...form, brokerId: e.target.value})}>
                                    <option value="">None</option>
                                    {brokers.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                </select>
                            </div>
                            <div className="col-6 col-md-2">
                                <label className="small fw-bold">Terms</label>
                                <input type="number" className="form-control" placeholder="Days" value={form.paymentTermsDays} onChange={e => setForm({...form, paymentTermsDays: e.target.value})} />
                            </div>
                        </div>

                        {/* ROW 2: STOCK SELECTION */}
                        <div className="card p-3 mb-3 border bg-white">
                            <label className="small fw-bold text-muted mb-1">Select Rough Lot to Sell</label>
                            <select className="form-select mb-3" onChange={handleLotSelect} required>
                                <option value="">-- Choose from Inventory --</option>
                                {stock.map(l => (
                                    <option key={l._id} value={l._id}>
                                        {l.rpNumber} | {l.weight} Cts | {l.party?.companyName}
                                    </option>
                                ))}
                            </select>

                            {/* LOT INFO PREVIEW (Enhanced) */}
                            {form.lotId && (
                                <div className="alert alert-secondary p-2 mb-0">
                                    <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
                                        <small>Origin: <strong>{form.origin}</strong></small>
                                        <small>Sieve: <strong>{form.sieveSize}</strong></small>
                                        <small>Assort: <strong>{form.assortment}</strong></small>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <small>Weight: <strong>{form.weight} Cts</strong></small>
                                        <small>Cost: <strong>{Math.round(form.costRate)} /ct</strong></small>
                                        <small>Val: <strong>{Math.round(form.costTotal).toLocaleString()}</strong></small>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ROW 3: PRICING */}
                        <div className="row mb-3">
                            <div className="col-4 col-md-3">
                                <label className="fw-bold text-primary">Sale Rate</label>
                                <input type="number" step="0.01" className="form-control fw-bold" value={form.saleRate} onChange={e => setForm({...form, saleRate: e.target.value})} required />
                            </div>
                             <div className="col-4 col-md-2">
                                <label className="small fw-bold">Currency</label>
                                <select className="form-select" value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}>
                                    <option>INR</option><option>USD</option>
                                </select>
                            </div>
                            {form.currency === 'USD' && (
                                <div className="col-4 col-md-2">
                                    <label className="small fw-bold text-danger">Ex. Rate</label>
                                    <input type="number" className="form-control" value={form.exchangeRate} onChange={e => setForm({...form, exchangeRate: e.target.value})} />
                                </div>
                            )}
                            <div className="col-6 col-md-2">
                                <label className="small">Tax %</label>
                                <input type="number" className="form-control" value={form.taxPercent} onChange={e => setForm({...form, taxPercent: e.target.value})} disabled={form.billType === 'Cash'} />
                            </div>
                             <div className="col-6 col-md-3">
                                <label className="small">Extra Charges</label>
                                <input type="number" className="form-control" value={form.extraCharges} onChange={e => setForm({...form, extraCharges: e.target.value})} />
                            </div>
                        </div>

                        {/* ROW 4: TOTALS & PROFIT */}
                        <div className="card border-0 shadow-sm mb-3">
                            <div className="card-body p-2">
                                <div className="row text-center align-items-center">
                                    <div className="col-4">
                                        <small className="d-block text-muted">Invoice Amount</small>
                                        <h5 className="mb-0 fw-bold text-primary">
                                            {form.currency === 'USD' ? '$' : '₹'} {Math.round(calc.finalAmount).toLocaleString()}
                                        </h5>
                                    </div>
                                    <div className="col-4 border-start border-end">
                                        <small className="d-block text-muted">Est. Profit (INR)</small>
                                        <h5 className={`mb-0 fw-bold ${calc.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {Math.round(calc.profit).toLocaleString()}
                                        </h5>
                                    </div>
                                    <div className="col-4">
                                        <small className="d-block text-muted">Margin</small>
                                        <strong className={`${calc.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {calc.profitPercent.toFixed(1)}%
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button className="btn btn-primary w-100 py-2 fw-bold">GENERATE INVOICE</button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RoughSale;